import { TGoogleRoutePoint } from "../../../api/types/Map";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { approxMetersBetween } from "../multiDay/geo";

/**
 * Pure functional core for judging the quality of a road-aligned route and
 * for producing an honest fallback when no road-alignment method gives a
 * plausible answer.
 *
 * Nothing in this file performs IO, reads globals, or mutates its inputs.
 * Everything is deterministic: the same points in always give the same
 * verdict out. The IO wrappers (the Snap-to-Road / Directions fetchers and
 * the render orchestrator) call into these functions to decide what to draw.
 *
 * Why this exists
 * ---------------
 * Snap-to-Road almost never fails loudly. On a dense grid of parallel
 * residential streets, or for a near-stationary asset, it returns HTTP 200
 * with a snappedPoints array that has been matched onto the wrong parallel
 * road - consecutive device fixes land one street over, flip-flopping
 * between near-identical placeIds. The old pipeline accepted any non-empty
 * result, so those mis-snapped fixes were drawn verbatim and the straight
 * connections between them cut across houses. The detour filter upstream
 * only guards INTERPOLATED stretches between two real fixes; it never
 * questions the real fixes themselves, which is exactly where this failure
 * lives. These helpers add that missing quality gate.
 */

export type RouteCoordinate = {
  latitude: number;
  longitude: number;
};

// A fix at (0,0) is the null-island sentinel a device emits when it has no
// GPS lock, not a real position off the coast of Africa. Anything outside
// these bounds is not a valid WGS84 coordinate at all.
const MIN_VALID_LATITUDE = -90;
const MAX_VALID_LATITUDE = 90;
const MIN_VALID_LONGITUDE = -180;
const MAX_VALID_LONGITUDE = 180;

// Consecutive fixes implying a speed above this are physically impossible
// for a ground vehicle and are a GPS glitch - a single fix teleporting a
// town away and back. Dropping it keeps the glitch from drawing a spike and,
// just as importantly, from skewing the segmenter's distance percentiles
// (which decide DENSE vs SPARSE, and therefore snap vs directions). Well
// above any real road speed so a genuinely fast vehicle is never dropped.
const MAX_PLAUSIBLE_GROUND_SPEED_KMPH = 320;
// Below this elapsed time, two fixes can't yield a meaningful speed (clock
// resolution is ~1s), so a jump check on them would be noise - a repositioned
// fix in the same second is handled by the coordinate/▲distance checks, not
// the speed check.
const MIN_SECONDS_FOR_SPEED_CHECK = 1;

const isValidCoordinate = (point: {
  latitude: number;
  longitude: number;
}): boolean => {
  const { latitude, longitude } = point;
  if (
    latitude === null ||
    longitude === null ||
    latitude === undefined ||
    longitude === undefined ||
    Number.isNaN(latitude) ||
    Number.isNaN(longitude)
  )
    return false;
  // exact (0,0) is the no-lock sentinel
  if (latitude === 0 && longitude === 0) return false;
  return (
    latitude >= MIN_VALID_LATITUDE &&
    latitude <= MAX_VALID_LATITUDE &&
    longitude >= MIN_VALID_LONGITUDE &&
    longitude <= MAX_VALID_LONGITUDE
  );
};

export type RoutePlausibilityVerdict = {
  isPlausible: boolean;
  // human-readable reason, surfaced only in debug logging
  reason: string;
  // worst single snapped-vs-source drift observed, in metres
  maxDriftMeters: number;
  // road length / straight-line length across all original fixes
  inflationRatio: number;
};

// How far a snapped ORIGINAL fix is allowed to sit from the device fix it
// came from before we call it mis-snapped. Roads API legitimately nudges a
// fix a few metres onto the centre line; a jump to the next street over is
// tens of metres. A residential block is ~60-100m across, so a drift beyond
// this is the parallel-street error, not centre-line snapping.
export const MAX_SNAPPED_FIX_DRIFT_METERS = 35;

// A snapped original fix that drifts past the hard limit is a mis-snap no
// matter what the rest of the path looks like - treat the result as
// implausible immediately.
export const SNAPPED_FIX_DRIFT_HARD_LIMIT_METERS = 55;

// How much longer the whole snapped road path may be than the straight-line
// distance through the same device fixes.
//
// This is a BACKSTOP, not the primary signal - the per-fix drift check above
// is the precise one. Inflation alone is a poor signal because it has two
// innocent causes that look identical to a mis-snap:
//   1. real road curvature - a long bend or a cul-de-sac the vehicle actually
//      drove adds a lot of length over the straight chord between sparse fixes.
//   2. thinning - fixes are decimated before snapping, so the straight-line
//      baseline is measured through fewer points than the snapped path has,
//      which inflates the ratio even for a faithful snap.
// So the threshold is set high enough that only GROSS inflation trips it, and
// it is additionally required to coincide with at least one drifting fix (see
// usage) so a faithfully-snapped curvy road is never rejected on length alone.
export const MAX_ROUTE_INFLATION_RATIO = 4;

const isOriginalFix = (point: {
  originalIndex?: number | null;
}): boolean =>
  point.originalIndex !== undefined && point.originalIndex !== null;

const coordinateOf = (point: {
  location: RouteCoordinate;
}): { lat: number; lng: number } => ({
  lat: point.location.latitude,
  lng: point.location.longitude,
});

const straightLineLengthMeters = (
  coordinates: { lat: number; lng: number }[]
): number => {
  let total = 0;
  for (let index = 1; index < coordinates.length; index++) {
    total += approxMetersBetween(
      coordinates[index - 1],
      coordinates[index]
    );
  }
  return total;
};

/**
 * Judges whether a snapped route plausibly represents the path the device
 * actually reported.
 *
 * The decisive signal is per-fix drift: every snapped point that carries an
 * `originalIndex` is compared against the raw device fix it was snapped
 * from. A snapped fix that lands tens of metres away has been matched onto a
 * different road than the one the device was on - the parallel-street error.
 *
 * A secondary signal is total inflation: the full snapped path length versus
 * the straight-line length through the device fixes. This catches a result
 * that drifted consistently rather than at one obvious point.
 *
 * `snappedRoutePoints` is the Snap-to-Road output (original + interpolated).
 * `sourceTripPoints` is the device fixes that were sent to be snapped.
 */
export const snappedPathPlausibility = (
  snappedRoutePoints: TGoogleRoutePoint[],
  sourceTripPoints: VehicleMapDataPoint[]
): RoutePlausibilityVerdict => {
  // Nothing to judge - an empty or trivial result is handled by the caller's
  // length checks, not here. Report it implausible so the caller steps down.
  if (!snappedRoutePoints || snappedRoutePoints.length < 2) {
    return {
      isPlausible: false,
      reason: "snapped result has fewer than 2 points",
      maxDriftMeters: Infinity,
      inflationRatio: Infinity,
    };
  }

  let maxDriftMeters = 0;
  let driftViolationCount = 0;
  let comparedFixCount = 0;

  snappedRoutePoints.forEach((snappedPoint) => {
    if (!isOriginalFix(snappedPoint)) return;
    const sourceTripPoint = snappedPoint.tripPoint as
      | VehicleMapDataPoint
      | undefined;
    if (!sourceTripPoint) return;

    comparedFixCount += 1;
    const driftMeters = approxMetersBetween(
      {
        lat: sourceTripPoint.latitude,
        lng: sourceTripPoint.longitude,
      },
      coordinateOf(snappedPoint)
    );
    if (driftMeters > maxDriftMeters) maxDriftMeters = driftMeters;
    if (driftMeters > MAX_SNAPPED_FIX_DRIFT_METERS) driftViolationCount += 1;

    // one egregious jump is enough to condemn the whole result
    if (driftMeters > SNAPPED_FIX_DRIFT_HARD_LIMIT_METERS) {
      driftViolationCount = Math.max(driftViolationCount, 1);
    }
  });

  // total inflation of the road path over the straight line through the
  // device fixes
  const sourceCoordinates = sourceTripPoints.map((tripPoint) => ({
    lat: tripPoint.latitude,
    lng: tripPoint.longitude,
  }));
  const snappedCoordinates = snappedRoutePoints.map(coordinateOf);
  const straightLineMeters = straightLineLengthMeters(sourceCoordinates);
  const snappedLineMeters = straightLineLengthMeters(snappedCoordinates);
  const inflationRatio =
    straightLineMeters > 0 ? snappedLineMeters / straightLineMeters : 1;

  const hasHardDrift = maxDriftMeters > SNAPPED_FIX_DRIFT_HARD_LIMIT_METERS;
  // a lone borderline drift is tolerated; a repeated pattern of drift is the
  // mis-snap signature
  const hasRepeatedDrift =
    comparedFixCount > 0 &&
    driftViolationCount / comparedFixCount > 0.15 &&
    driftViolationCount >= 2;
  // inflation is only trusted as a backstop WHEN it coincides with at least
  // one drifting fix - gross length plus a fix off its source road is a
  // mis-snap, whereas gross length on its own is almost always real curvature
  // or a thinning artefact (see MAX_ROUTE_INFLATION_RATIO).
  const isOverInflated =
    inflationRatio > MAX_ROUTE_INFLATION_RATIO && driftViolationCount >= 1;

  if (hasHardDrift || hasRepeatedDrift || isOverInflated) {
    const reasons: string[] = [];
    if (hasHardDrift)
      reasons.push(
        `fix drift ${maxDriftMeters.toFixed(1)}m > ${SNAPPED_FIX_DRIFT_HARD_LIMIT_METERS}m`
      );
    if (hasRepeatedDrift)
      reasons.push(
        `${driftViolationCount}/${comparedFixCount} fixes drifted > ${MAX_SNAPPED_FIX_DRIFT_METERS}m`
      );
    if (isOverInflated)
      reasons.push(
        `inflation ${inflationRatio.toFixed(2)}x > ${MAX_ROUTE_INFLATION_RATIO}x`
      );
    return {
      isPlausible: false,
      reason: reasons.join("; "),
      maxDriftMeters,
      inflationRatio,
    };
  }

  return {
    isPlausible: true,
    reason: "within drift and inflation limits",
    maxDriftMeters,
    inflationRatio,
  };
};

/**
 * The honest floor. When every road-alignment method is empty or
 * implausible, draw the raw device fixes as a straight-line polyline. It is
 * angular through corners at a 5s reporting interval, but it only ever shows
 * where the device actually reported - never a fabricated detour through a
 * house or down the wrong street.
 */
export const straightLineRoutePoints = (
  sourceTripPoints: VehicleMapDataPoint[]
): TGoogleRoutePoint[] =>
  sourceTripPoints.map((tripPoint, pointIndex) => ({
    location: {
      latitude: tripPoint.latitude,
      longitude: tripPoint.longitude,
    },
    originalIndex: pointIndex,
    tripPoint,
  }));

/**
 * Cleans a raw trip's GPS fixes before anything tries to route them.
 *
 * This runs ahead of trip evaluation on purpose. The segmenter derives its
 * DENSE / MODERATE / SPARSE thresholds from the DISTRIBUTION of inter-fix
 * distances, so a single teleporting glitch doesn't just draw one bad spike -
 * it inflates the percentiles and can misclassify the whole trip, sending
 * dense urban data to Directions (which fabricates a route) or sparse data to
 * Snap (which snaps onto the wrong street). Removing invalid and impossible
 * fixes first protects every downstream decision.
 *
 * Two kinds of fix are removed:
 *   - invalid coordinates: null/NaN/out-of-range, and the exact (0,0)
 *     no-GPS-lock sentinel.
 *   - teleport glitches: a fix implying an impossible ground speed from the
 *     last kept fix. The jump is attributed to the newer fix, so a lone bad
 *     reading is dropped while the track either side of it is preserved.
 *
 * Pure: returns a new array, never mutates the input, no IO.
 */
export const sanitizeTripPointsForRouting = (
  tripPoints: VehicleMapDataPoint[]
): VehicleMapDataPoint[] => {
  if (!tripPoints || tripPoints.length === 0) return [];

  const coordinateValid = tripPoints.filter(isValidCoordinate);
  if (coordinateValid.length < 2) return coordinateValid;

  const kept: VehicleMapDataPoint[] = [coordinateValid[0]];
  for (let index = 1; index < coordinateValid.length; index++) {
    const previousKept = kept[kept.length - 1];
    const candidate = coordinateValid[index];

    const elapsedSeconds = candidate.timestamp - previousKept.timestamp;
    // out-of-order or same-second fixes can't be speed-checked; keep them and
    // let the coordinate checks be the only guard there
    if (elapsedSeconds < MIN_SECONDS_FOR_SPEED_CHECK) {
      kept.push(candidate);
      continue;
    }

    const metersMoved = approxMetersBetween(
      { lat: previousKept.latitude, lng: previousKept.longitude },
      { lat: candidate.latitude, lng: candidate.longitude }
    );
    const impliedSpeedKmph = (metersMoved / elapsedSeconds) * 3.6;
    if (impliedSpeedKmph > MAX_PLAUSIBLE_GROUND_SPEED_KMPH) {
      // teleport glitch - drop the candidate, keep comparing the next fix
      // against the last good one
      continue;
    }
    kept.push(candidate);
  }
  return kept;
};

export type BridgePlausibilityVerdict = {
  isPlausible: boolean;
  reason: string;
};

// A stitch bridge between two chunk boundaries is a Directions route. If the
// two boundary points are close, any road path between them is fine; if they
// are far apart or separated by a barrier, Directions will connect them with
// a multi-block detour that the vehicle never drove. Same shape of test as
// the interpolated-detour filter: the road path may be somewhat longer than
// the straight line, but not wildly.
export const MAX_BRIDGE_DETOUR_RATIO = 2.5;
export const MIN_BRIDGE_DETOUR_SLACK_METERS = 60;

/**
 * Judges whether a Directions "bridge" between two chunk boundaries is a
 * plausible connector or a fabricated detour. `bridgeRoadMeters` is the
 * length of the Directions path; the two endpoints are the chunk boundary
 * points the bridge connects.
 */
export const bridgePlausibility = (
  bridgeRoadMeters: number,
  fromPoint: { latitude: number; longitude: number },
  toPoint: { latitude: number; longitude: number }
): BridgePlausibilityVerdict => {
  const straightMeters = approxMetersBetween(
    { lat: fromPoint.latitude, lng: fromPoint.longitude },
    { lat: toPoint.latitude, lng: toPoint.longitude }
  );
  const allowed = Math.max(
    straightMeters * MAX_BRIDGE_DETOUR_RATIO,
    straightMeters + MIN_BRIDGE_DETOUR_SLACK_METERS
  );
  if (bridgeRoadMeters > allowed) {
    return {
      isPlausible: false,
      reason: `bridge ${bridgeRoadMeters.toFixed(1)}m > allowed ${allowed.toFixed(1)}m (straight ${straightMeters.toFixed(1)}m)`,
    };
  }
  return { isPlausible: true, reason: "within bridge detour limit" };
};
